/* eslint-disable id-blacklist */
import { areWeTestingWithJest } from "@buildwithflux/shared";
import cloneDeep from "lodash/cloneDeep";
// QUESTION: why does this wrapper exist?
// TODO: We should not wrap it, think this exists because its trying to be
// lazy and get rid of all the typing here. `cloneDeep` doesn't throw away
// typing.
/**
 * @deprecated Use `cloneDeep` from lodash instead
 */
export function deepCopy(data) {
    return cloneDeep(data);
}
/**
 * Takes an interface and creates a flattened object out of it (converts maps to object properties)
 *
 * This function cannot detect cyclic data structures, and will cause a stack overflow if it encounters one. We have
 * little hope of being able to fix this, as usual 'decycle' approaches (like https://github.com/douglascrockford/JSON-js/blob/7e83f38a2312429fd4933169c1f6a27fd65e889c/cycle.js#L29)
 * rely on being able to form a JSONPath for the self-reference, and we can't do so generally for Maps, which can have
 * complex non-string keys.
 *
 * NOTE: see inverse flatObjectToInterface
 * HACK: this function requires type casting output
 */
export function interfaceDataToFlatObject(data) {
    if (data === undefined) {
        return null;
    }
    else if (data === null || Number.isNaN(data) || data === Infinity) {
        return data;
    }
    const obj = {};
    if (data instanceof Array) {
        // Make sure an empty array also gets the data structure indicator
        // otherwise empty arrays return as empty objects
        obj["_dataStructureIsArray"] = true;
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        data.forEach((value, key) => {
            obj[key.toString()] = interfaceDataToFlatObject(value);
        });
    }
    else if (data instanceof Map) {
        // QUESTION: why is dataStructureIsMap not underscored like the rest?
        obj["dataStructureIsMap"] = true;
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        data.forEach((value, key) => {
            obj[key.toString()] = interfaceDataToFlatObject(value);
        });
        return obj;
    }
    else if (data instanceof Set) {
        obj["_dataStructureIsSet"] = true;
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        data.forEach((value, key) => {
            obj[key.toString()] = interfaceDataToFlatObject(value);
        });
        return obj;
    }
    else if (data instanceof Object) {
        for (const [dataKey, dataValue] of Object.entries(data)) {
            if (dataValue instanceof Map) {
                const mapObj = {};
                mapObj["dataStructureIsMap"] = true;
                // eslint-disable-next-line @buildwithflux/no-array-foreach
                dataValue.forEach((value, key) => {
                    mapObj[key.toString()] = interfaceDataToFlatObject(value);
                });
                obj[dataKey.toString()] = mapObj;
            }
            else if (dataValue instanceof Object) {
                obj[dataKey] = interfaceDataToFlatObject(dataValue);
            }
            else {
                obj[dataKey] = dataValue;
            }
        }
    }
    else {
        return data;
    }
    return obj;
}
let flatObjectToInterfaceWarned = false;
/**
 * Flatten object without maps or arrays
 *
 * Comments from @ChristianBlank:
 * The new property declares if it is an array or not to be able to convert it
 * back from a string object to a nested object. The issue is that firebase
 * can't merge arrays so we convert them to maps to be able to replace single
 * entries in an Array.
 *
 * Since when the array is [1,2,3] and 2 gets replaced Firebase does not know if
 * the entry 2 was in position 0 or 2, so it overwrites the whole array every
 * time which lead to more updates on the receiving client than required.
 *
 * Later comments:
 * This function cannot detect cyclic data structures, and will cause a stack overflow if it encounters one. We have
 * little hope of being able to fix this, as usual 'decycle' approaches (like https://github.com/douglascrockford/JSON-js/blob/7e83f38a2312429fd4933169c1f6a27fd65e889c/cycle.js#L29)
 * rely on being able to form a JSONPath for the self-reference, and we can't do so generally for Maps, which can have
 * complex non-string keys.
 *
 * Later still:
 * We now pass an options object to this function to allow for the warning to be turned off. This is because we want
 * to be able to migrate away from this function and remove it. But, we can't do that until we have round-tripped all
 * possible data (e.g. to convert arrays that have been turned into objects with the _dataStructureIsArray property back
 * into native arrays)
 *
 * NOTE: see inverse interfaceDataToFlatObject
 * HACK: this function requires type casting output
 */
export function flatObjectToInterface(object, options = {}) {
    if (object instanceof Array || object instanceof Map || object instanceof Set) {
        // If we are passing an object containing array/map/set to this function, the array/map/set
        // will be converted to an object... which is not what we expected. So in those cases we
        // just return the object directly.
        // For example. Without this check { positions: [{x: 1, y: 1}, {x: 2, y: 2}] } will be converted to
        // { 0: {x: 1, y: 1}, 1: {x: 2, y: 2}}.
        if (!flatObjectToInterfaceWarned && !areWeTestingWithJest() && options?.warnOnNonFlatObject !== false) {
            // eslint-disable-next-line no-console -- allow legacy console statements
            console.warn("Passing non-flat object to flatObjectToInterface converter", object);
            flatObjectToInterfaceWarned = true;
        }
        return object;
    }
    if (object === undefined) {
        return null;
    }
    else if (object === null || Number.isNaN(object) || object === Infinity) {
        return object;
    }
    const obj = {};
    if (object._dataStructureIsArray) {
        return createArray(object, options);
    }
    else if (object.dataStructureIsMap) {
        return createMap(object, options);
    }
    else if (object._dataStructureIsSet) {
        return createSet(object, options);
    }
    else if (typeof object !== "object") {
        return object;
    }
    else {
        for (const [objKey, objValue] of Object.entries(object)) {
            // Check for null otherwise function fails since it can't read _dataStructureIsArray of null
            if (objValue === undefined || objValue === null) {
                // Replace undefined with null
                obj[objKey] = null;
                // @ts-ignore
            }
            else if (objValue._dataStructureIsArray) {
                obj[objKey] = createArray(objValue, options);
            }
            else if (objValue.dataStructureIsMap &&
                (objKey === "properties" ||
                    objKey === "roles" ||
                    objKey === "elements" ||
                    objKey === "routes" ||
                    objKey === "terminals" ||
                    objKey === "middleVertices")) {
                // This is to prevent loading indexed object types objects as es6 maps
                // TODO: Once we are done migrating away from es6 maps it would makes sense to clean this up
                delete objValue.dataStructureIsMap;
                obj[objKey] = flatObjectToInterface(objValue, options);
            }
            else if (objValue.dataStructureIsMap) {
                obj[objKey] = createMap(objValue, options);
            }
            else if (objValue instanceof Object) {
                obj[objKey] = flatObjectToInterface(objValue, options);
            }
            else {
                obj[objKey] = objValue;
            }
        }
    }
    return obj;
}
function createMap(object, options) {
    const result = new Map();
    if (object.dataStructureIsMap) {
        delete object.dataStructureIsMap;
    }
    for (const [key, value] of Object.entries(object)) {
        result.set(key, flatObjectToInterface(value, options));
    }
    return result;
}
function createArray(object, options) {
    const result = [];
    if (object._dataStructureIsArray) {
        delete object._dataStructureIsArray;
    }
    for (const value of Object.values(object)) {
        if (typeof value === "number" || typeof value === "string" || typeof value === "boolean") {
            result.push(value);
        }
        else {
            result.push(flatObjectToInterface(value, options));
        }
    }
    return result;
}
function createSet(object, options) {
    const result = new Set();
    if (object._dataStructureIsSet) {
        delete object._dataStructureIsSet;
    }
    // @ts-ignore
    for (const value of Object.values(object)) {
        result.add(flatObjectToInterface(value, options));
    }
    return result;
}
//# sourceMappingURL=dataStructure.js.map